import { useMemo, useState } from 'react';
import { MoreVertical, Pencil, Plus, Search, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { SelectNativo } from '@/components/ui/select-nativo';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { AvatarDePersona } from '@/components/ui/avatar-de-persona';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { esEmpresaDemo } from '@/features/mini-prixia/empresa-demo';
import { AvisoDemo } from '@/features/mini-prixia/components/aviso-demo';
import { useCarga } from '@/features/dynamic-pricing/monitoreo/hooks/use-carga';
import { customRoleService } from '@/features/permissions/services/custom-role-service';
import { organizationService } from '@/features/organizations/services/organization-service';
import { userService } from '@/features/users/services/user-service';
import { ResetPasswordDialog } from '@/features/users/components/reset-password-dialog';
import { InvitationLinksCard } from '@/features/users/components/invitation-links-card';
import { toast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils/cn';
import { TablaQueSeApila, type ColumnaApilable } from '@/components/patterns/tabla-que-se-apila';
import { MarcoDeMiEmpresa } from '../components/marco-de-mi-empresa';
import { haceCuantoEntro } from '../lib/secciones';
import { colorDeCargo } from '../lib/cargos';
import {
  miEmpresaService,
  type EstadoDePersona,
  type PersonaDeLaEmpresa,
} from '../services/mi-empresa-service';
import {
  AVISO_SOPORTE_NO_PUEDE,
  atributosSoporteNoPuede,
  useEnSoporte,
} from '@/features/mini-prixia/soporte-solo-mira';

const ESTADO: Record<EstadoDePersona, { texto: string; clase: string }> = {
  ACTIVO: { texto: 'Activo', clase: 'text-ok' },
  INVITADO: { texto: 'Invitación enviada', clase: 'text-alerta' },
  INVITACION_VENCIDA: { texto: 'Invitación vencida', clase: 'text-bloqueo' },
  INACTIVO: { texto: 'Desactivado', clase: 'text-muted-foreground' },
};

/** Los roles base, para quien no tiene un rol propio de la empresa. */
const ROLES_BASE = [
  { valor: 'COMPANY_ADMIN', texto: 'Administrador' },
  { valor: 'SALESPERSON', texto: 'Vendedor' },
  { valor: 'APPROVER', texto: 'Aprobador' },
] as const;

/** Mi empresa › Usuarios (maqueta de Melanie, 25-oct). */
export function UsuariosSection() {
  const { user } = useAuth();
  const subscription = useAuthStore((s) => s.subscription);
  const personas = useCarga(() => miEmpresaService.personas(), []);
  /* Demo (Ronda Z): el tope de personas. Una empresa común no lo pide. */
  const cupo = useCarga(
    () => (esEmpresaDemo() ? miEmpresaService.cupoDePersonas() : Promise.resolve(null)),
    []
  );
  const cargos = useCarga(() => customRoleService.getAll(), []);
  const equipos = useCarga(() => organizationService.list(), []);

  const [busqueda, setBusqueda] = useState('');
  const [rol, setRol] = useState('');
  const [equipo, setEquipo] = useState('');
  const [dialogo, setDialogo] = useState<
    { modo: 'invitar' } | { modo: 'editar'; persona: PersonaDeLaEmpresa } | null
  >(null);
  const [aRestablecer, setARestablecer] = useState<PersonaDeLaEmpresa | null>(null);

  const lista = personas.datos ?? [];
  const nombresDeCargo = useMemo(() => [...new Set(lista.map((p) => p.cargo))].sort(), [lista]);
  const filtradas = lista.filter((p) => {
    const q = busqueda.trim().toLowerCase();
    if (q && !`${p.nombre} ${p.apellido} ${p.email}`.toLowerCase().includes(q)) return false;
    if (rol && p.cargo !== rol) return false;
    if (equipo && !p.equipos.some((e) => e.id === equipo)) return false;
    return true;
  });
  const invitaciones = lista.filter(
    (p) => p.estado === 'INVITADO' || p.estado === 'INVITACION_VENCIDA'
  ).length;
  const activas = lista.filter((p) => p.estado !== 'INACTIVO').length;
  const limite = subscription?.limits?.maxUsers;
  // En una demo manda el tope de la demo: el límite del plan de prueba se contradeciría a la vista.
  const esDemo = esEmpresaDemo();
  const textoDelPlan = esDemo
    ? cupo.datos
      ? `prueba · ${cupo.datos.usadas} de ${cupo.datos.tope}`
      : null
    : subscription
      ? `plan ${subscription.planName} ${limite && limite > 0 && limite < 9999 ? `· ${activas} de ${limite}` : 'sin límite'}`
      : null;

  const alTope = !!cupo.datos && cupo.datos.usadas >= cupo.datos.tope;
  // Soporte no invita ni cambia personas en NINGUNA empresa (`frenarEscriturasASoporte`).
  const enSoporte = useEnSoporte();
  const textoDelTope = cupo.datos
    ? `En la prueba podés tener hasta ${cupo.datos.tope} personas, contando las invitaciones pendientes, y ya llegaste. Con el plan completo sumás a todo tu equipo.`
    : '';
  /** Lo que cambia la cuenta de personas (invitar, reactivar, desactivar). */
  const recargarTodo = () => {
    personas.recargar();
    cupo.recargar();
  };

  const reenviar = async (p: PersonaDeLaEmpresa) => {
    try {
      await miEmpresaService.reenviar(p.id);
      toast({ title: `Invitación reenviada a ${p.email}`, variant: 'success' });
      recargarTodo();
    } catch (e: any) {
      toast({
        title: 'No se pudo reenviar',
        description: e?.response?.data?.error ?? e?.message,
        variant: 'destructive',
      });
    }
  };

  const activar = async (p: PersonaDeLaEmpresa, activa: boolean) => {
    try {
      if (activa) await userService.updateUser(p.id, { isActive: true });
      else await userService.deleteUser(p.id);
      toast({
        title: activa ? `${p.nombre} vuelve a tener acceso` : `${p.nombre} ya no puede entrar`,
      });
      recargarTodo();
    } catch (e: any) {
      toast({
        title: 'No se pudo cambiar',
        description: e?.response?.data?.error ?? e?.message,
        variant: 'destructive',
      });
    }
  };

  /** Lo que usan varias celdas de una fila. */
  const datosDeLaFila = (p: PersonaDeLaEmpresa) => ({
    nombre: `${p.nombre} ${p.apellido}`.trim(),
    estado: ESTADO[p.estado],
    yo: p.id === user?.id,
  });
  const celdaUsuario = (p: PersonaDeLaEmpresa) => {
    const { nombre, yo } = datosDeLaFila(p);
    return (
      <span className="flex items-center gap-3">
        <AvatarDePersona nombre={nombre} clave={p.id} className="size-10 text-sm" />
        <span className="min-w-0">
          <span className="block truncate text-[15px] font-semibold">
            {nombre}
            {yo && <span className="font-normal text-muted-foreground"> (tú)</span>}
          </span>
          <span className="block truncate text-sm text-muted-foreground">{p.email}</span>
        </span>
      </span>
    );
  };
  const celdaRol = (p: PersonaDeLaEmpresa) => (
    <span
      className={cn(
        'inline-flex rounded-full px-3 py-1 text-sm font-semibold',
        colorDeCargo(p.cargo)
      )}
    >
      {p.cargo}
    </span>
  );
  const celdaEquipo = (p: PersonaDeLaEmpresa) =>
    p.equipos.map((e) => e.name).join(', ') || <span className="text-muted-foreground">—</span>;
  const celdaEstado = (p: PersonaDeLaEmpresa) => {
    const { estado } = datosDeLaFila(p);
    return (
      <span
        className={cn('inline-flex items-center gap-2 text-[15px] font-semibold', estado.clase)}
      >
        <span className="size-2 rounded-full bg-current" aria-hidden="true" />
        {estado.texto}
      </span>
    );
  };
  const celdaAcceso = (p: PersonaDeLaEmpresa) =>
    p.estado === 'INVITADO' || p.estado === 'INVITACION_VENCIDA'
      ? 'todavía no entró'
      : haceCuantoEntro(p.ultimoAcceso);
  const celdaAcciones = (p: PersonaDeLaEmpresa) => {
    const { nombre, yo } = datosDeLaFila(p);
    return (
      <span
        data-tour={p.id === filtradas[0]?.id ? 'users-actions' : undefined}
        className="inline-flex items-center gap-1"
      >
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setDialogo({ modo: 'editar', persona: p })}
          aria-label={`Editar a ${nombre}`}
          {...atributosSoporteNoPuede(enSoporte)}
        >
          <Pencil className="size-4" aria-hidden="true" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={`Más acciones para ${nombre}`}>
              <MoreVertical className="size-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {enSoporte && (
              <p className="max-w-[16rem] px-2 py-1.5 text-xs text-muted-foreground">
                {AVISO_SOPORTE_NO_PUEDE}
              </p>
            )}
            {(p.estado === 'INVITADO' || p.estado === 'INVITACION_VENCIDA') && (
              <DropdownMenuItem disabled={enSoporte} onSelect={() => reenviar(p)}>
                Reenviar invitación
              </DropdownMenuItem>
            )}
            {p.estado !== 'INACTIVO' && (
              <DropdownMenuItem disabled={enSoporte} onSelect={() => setARestablecer(p)}>
                Restablecer contraseña
              </DropdownMenuItem>
            )}
            {!yo &&
              (p.estado === 'INACTIVO' ? (
                <DropdownMenuItem disabled={enSoporte} onSelect={() => activar(p, true)}>
                  Volver a dar acceso
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  className="text-destructive"
                  disabled={enSoporte}
                  onSelect={() => activar(p, false)}
                >
                  Quitar acceso
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    );
  };
  /** Las columnas de la tarjeta del teléfono: la persona arriba, sus acciones al lado. */
  const columnasEnTarjeta: Array<ColumnaApilable<PersonaDeLaEmpresa>> = [
    { id: 'usuario', encabezado: 'Usuario', enTarjeta: 'titulo', celda: celdaUsuario },
    { id: 'acciones', encabezado: null, enTarjeta: 'acciones', celda: celdaAcciones },
    { id: 'estado', encabezado: 'Estado', celda: celdaEstado },
    { id: 'rol', encabezado: 'Rol', celda: celdaRol },
    { id: 'equipo', encabezado: 'Equipo', celda: celdaEquipo },
    { id: 'acceso', encabezado: 'Último acceso', celda: celdaAcceso },
  ];

  return (
    <MarcoDeMiEmpresa
      seccion="usuarios"
      dataTour="users-header"
      acciones={
        <Button
          variant="degradado"
          size="lg"
          className="px-6"
          data-tour="users-create-btn"
          onClick={() => setDialogo({ modo: 'invitar' })}
          disabled={alTope || enSoporte}
          title={enSoporte ? AVISO_SOPORTE_NO_PUEDE : alTope ? textoDelTope : undefined}
        >
          <Plus className="size-4" aria-hidden="true" />
          Invitar usuario
        </Button>
      }
    >
      {alTope && <AvisoDemo texto={textoDelTope} />}
      <section
        aria-label="Usuarios de la empresa"
        className="overflow-hidden rounded-tarjeta border bg-card shadow-card"
      >
        <div className="flex flex-wrap items-center gap-3 border-b px-6 py-4">
          <div data-tour="users-search" className="relative w-full max-w-sm">
            <Search
              className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre o correo"
              aria-label="Buscar por nombre o correo"
              className="h-11 rounded-xl bg-lienzo pl-10"
            />
          </div>
          <label className="flex h-11 items-center gap-2 rounded-xl border px-3 text-sm text-muted-foreground">
            Rol
            <select
              value={rol}
              onChange={(e) => setRol(e.target.value)}
              className="bg-transparent font-semibold text-foreground focus-visible:outline-none"
            >
              <option value="">Todos</option>
              {nombresDeCargo.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <label className="flex h-11 items-center gap-2 rounded-xl border px-3 text-sm text-muted-foreground">
            Equipo
            <select
              value={equipo}
              onChange={(e) => setEquipo(e.target.value)}
              className="bg-transparent font-semibold text-foreground focus-visible:outline-none"
            >
              <option value="">Todos</option>
              {(equipos.datos ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </label>
          {personas.datos && (
            <p className="ml-auto text-sm text-muted-foreground">
              {lista.length} {lista.length === 1 ? 'usuario' : 'usuarios'}
              {invitaciones > 0 &&
                ` · ${invitaciones} ${invitaciones === 1 ? 'invitación pendiente' : 'invitaciones pendientes'}`}
              {textoDelPlan && ` · ${textoDelPlan}`}
            </p>
          )}
        </div>

        {personas.estado === 'error' && !personas.datos ? (
          <div className="p-6">
            <EstadoError onReintentar={personas.recargar}>
              No pudimos cargar los usuarios.
            </EstadoError>
          </div>
        ) : !personas.datos ? (
          <div className="flex flex-col gap-3 p-6" aria-busy="true">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : filtradas.length === 0 ? (
          <div className="p-6">
            <EstadoVacio
              icono={Users}
              titulo={lista.length ? 'Nadie con esos filtros' : 'Todavía no hay usuarios'}
            >
              {lista.length
                ? 'Prueba con otro rol o equipo.'
                : 'Invita a tu equipo con «Invitar usuario».'}
            </EstadoVacio>
          </div>
        ) : (
          <div data-tour="users-table">
            {/* Vault 349: en el teléfono, una tarjeta por persona (rol y estado a la vista). */}
            <TablaQueSeApila
              aria-label="Usuarios"
              columnas={columnasEnTarjeta}
              filas={filtradas}
              obtenerId={(p) => p.id}
              nombreDeFila={(p) => datosDeLaFila(p).nombre}
              tabla={
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[860px] border-collapse text-left">
                    <thead>
                      <tr className="border-b bg-muted/50 text-sm text-muted-foreground">
                        <th scope="col" className="py-3 pl-6 pr-4 font-medium">
                          Usuario
                        </th>
                        <th scope="col" className="px-4 py-3 font-medium">
                          Rol
                        </th>
                        <th scope="col" className="px-4 py-3 font-medium">
                          Equipo
                        </th>
                        <th scope="col" className="px-4 py-3 font-medium">
                          Estado
                        </th>
                        <th scope="col" className="px-4 py-3 font-medium">
                          Último acceso
                        </th>
                        <th scope="col" className="py-3 pr-6">
                          <span className="sr-only">Acciones</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtradas.map((p) => (
                        <tr key={p.id} className="border-b last:border-b-0">
                          <td className="py-3.5 pl-6 pr-4">{celdaUsuario(p)}</td>
                          <td className="px-4 py-3.5">{celdaRol(p)}</td>
                          <td className="px-4 py-3.5 text-[15px]">{celdaEquipo(p)}</td>
                          <td className="px-4 py-3.5">{celdaEstado(p)}</td>
                          <td className="px-4 py-3.5 text-[15px] text-muted-foreground">
                            {celdaAcceso(p)}
                          </td>
                          <td className="py-3.5 pr-6 text-right">{celdaAcciones(p)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              }
            />
          </div>
        )}
      </section>

      {dialogo && (
        <DialogoDePersona
          persona={dialogo.modo === 'editar' ? dialogo.persona : null}
          cargos={(cargos.datos ?? []).filter((c) => c.isActive)}
          equipos={equipos.datos ?? []}
          onCerrar={() => setDialogo(null)}
          onListo={() => {
            setDialogo(null);
            recargarTodo();
          }}
        />
      )}
      {/* Links de invitación por dominio: cada uno sirve UNA vez (Sin usar / Usada). Si el correo no llega, el enlace es la otra puerta (el servidor pide ser administrador, igual que esta pantalla). */}
      <div data-tour="users-invite-links" className="mt-6">
        <InvitationLinksCard />
      </div>
      <ResetPasswordDialog
        usuario={
          aRestablecer
            ? {
                id: aRestablecer.id,
                nombre: `${aRestablecer.nombre} ${aRestablecer.apellido}`.trim(),
                email: aRestablecer.email,
              }
            : null
        }
        onClose={() => setARestablecer(null)}
      />
    </MarcoDeMiEmpresa>
  );
}

/** Invitar (sin `persona`) o editar a alguien: nombre, rol y equipo. */
function DialogoDePersona({
  persona,
  cargos,
  equipos,
  onCerrar,
  onListo,
}: {
  persona: PersonaDeLaEmpresa | null;
  cargos: Array<{ id: string; name: string }>;
  equipos: Array<{ id: string; name: string }>;
  onCerrar: () => void;
  onListo: () => void;
}) {
  const [nombre, setNombre] = useState(persona?.nombre ?? '');
  const [apellido, setApellido] = useState(persona?.apellido ?? '');
  const [email, setEmail] = useState(persona?.email ?? '');
  // «c:<id>» es un rol de la empresa; «b:<ROL>», un rol base.
  const [rol, setRol] = useState(
    persona
      ? persona.customRoleId
        ? `c:${persona.customRoleId}`
        : `b:${persona.rol}`
      : cargos[0]
        ? `c:${cargos[0].id}`
        : 'b:SALESPERSON'
  );
  const equipoActual = persona?.equipos[0]?.id ?? '';
  const [equipo, setEquipo] = useState(equipoActual);
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    setGuardando(true);
    const customRoleId = rol.startsWith('c:') ? rol.slice(2) : null;
    const rolBase = rol.startsWith('b:')
      ? (rol.slice(2) as 'COMPANY_ADMIN' | 'SALESPERSON' | 'APPROVER')
      : undefined;
    try {
      if (!persona) {
        const r = await miEmpresaService.invitar({
          email: email.trim(),
          nombre: nombre.trim(),
          apellido: apellido.trim(),
          customRoleId,
          rol: rolBase,
          equipoId: equipo || null,
        });
        toast({
          title: r.correoEnviado
            ? `Invitación enviada a ${email.trim()}`
            : 'Se creó el usuario, pero el correo no salió',
          description: r.correoEnviado
            ? 'Tiene 7 días para crear su contraseña desde el enlace.'
            : 'Reenvía la invitación desde el menú de la persona.',
          variant: r.correoEnviado ? 'success' : 'destructive',
        });
      } else {
        await userService.updateUser(persona.id, {
          firstName: nombre.trim(),
          lastName: apellido.trim(),
          ...(customRoleId ? { customRoleId } : { customRoleId: null, role: rolBase as never }),
        });
        if (equipo !== equipoActual) {
          if (equipoActual) await organizationService.removeMember(equipoActual, persona.id);
          if (equipo) await organizationService.addMember(equipo, { userId: persona.id });
        }
        toast({ title: 'Cambios guardados', variant: 'success' });
      }
      onListo();
    } catch (e: any) {
      toast({
        title: persona ? 'No se pudo guardar' : 'No se pudo invitar',
        description: e?.response?.data?.error ?? e?.message,
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  };

  const valido = nombre.trim() && (persona || /^\S+@\S+\.\S+$/.test(email.trim()));
  return (
    <Dialog open onOpenChange={(a) => !a && onCerrar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{persona ? 'Editar usuario' : 'Invitar usuario'}</DialogTitle>
          <DialogDescription>
            {persona
              ? 'Su nombre, su rol y su equipo. El rol nuevo vale la próxima vez que entre.'
              : 'Le llega un correo para crear su contraseña y entrar a Prixia.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valido) void guardar();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="persona-nombre">Nombre</Label>
              <Input
                id="persona-nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="persona-apellido">Apellido</Label>
              <Input
                id="persona-apellido"
                value={apellido}
                onChange={(e) => setApellido(e.target.value)}
              />
            </div>
          </div>
          {!persona && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="persona-correo">Correo</Label>
              <Input
                id="persona-correo"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          )}
          <div className="flex flex-col gap-2">
            <Label htmlFor="persona-rol">Rol</Label>
            <SelectNativo id="persona-rol" value={rol} onChange={(e) => setRol(e.target.value)}>
              {cargos.length > 0 && (
                <optgroup label="Roles de tu empresa">
                  {cargos.map((c) => (
                    <option key={c.id} value={`c:${c.id}`}>
                      {c.name}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Roles base">
                {ROLES_BASE.map((r) => (
                  <option key={r.valor} value={`b:${r.valor}`}>
                    {r.texto}
                  </option>
                ))}
              </optgroup>
            </SelectNativo>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="persona-equipo">Equipo</Label>
            <SelectNativo
              id="persona-equipo"
              value={equipo}
              onChange={(e) => setEquipo(e.target.value)}
            >
              <option value="">Sin equipo</option>
              {equipos.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </SelectNativo>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onCerrar}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="degradado"
              disabled={!valido || guardando}
              isLoading={guardando}
            >
              {persona ? 'Guardar cambios' : 'Enviar invitación'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
